export const API_URL = process.env.REACT_APP_API_URL || '/api';
export const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8000' : '');

const getToken = () => {
    const userStr = localStorage.getItem('swapx_user');
    const user = userStr ? JSON.parse(userStr) : null;
    return user ? user.token : null;
};

const getLanguage = () => {
    return localStorage.getItem('language') || 'en';
};

const getRegion = () => {
    return localStorage.getItem('region') || 'GLOBAL';
};

const request = async (endpoint, options = {}) => {
    const token = getToken();
    const language = getLanguage();
    
    const headers = {
        'Accept-Language': language,
        ...options.headers,
    };

    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
    });

    if (response.status === 401) {
        if (token) {
            localStorage.removeItem('swapx_user');
            window.location.href = '/login';
        }
    }

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Something went wrong');
    }

    return response.json();
};

export const login = (email, password) => request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
});

export const register = (userData) => request('/auth/register', {
  method: 'POST',
  body: JSON.stringify(userData),
});

export const verifyEmail = (token) => request('/auth/verify-email', {
  method: 'POST',
  body: JSON.stringify({ token }),
});

export const getMe = () => {
    if (!getToken()) return Promise.resolve(null);
    return request('/auth/me');
};

export const getUserProfile = (id) => request(`/auth/profile/${id}`);

export const claimDailyReward = () => request('/auth/claim-daily', {
    method: 'POST',
});

export const updateMe = (data) => request('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
});

export const getCategories = () => request('/categories');

export const createCategory = (data) => request('/categories', {
    method: 'POST',
    body: JSON.stringify(data),
});

export const updateCategory = (id, data) => request(`/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
});

export const deleteCategory = (id) => request(`/categories/${id}`, {
    method: 'DELETE',
});

export const getItems = (filters = {}) => {
    const params = new URLSearchParams();
    params.append('region', getRegion());
    try {
        const stored = localStorage.getItem('user_geo_location');
        if (stored) {
            const { latitude, longitude, userLocation } = JSON.parse(stored);
            if (latitude && longitude && !filters.latitude) {
                params.append('latitude', latitude);
                params.append('longitude', longitude);
            }
            if (userLocation && !filters.userLocation) {
                params.append('userLocation', userLocation);
            }
        }
    } catch (e) {}
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            params.append(key, value);
        }
    });
    return request(`/items?${params.toString()}`);
};

export const getItem = (id) => request(`/items/${id}`);

export const createItem = (itemData) => {
    const formData = new FormData();
    formData.append('region', getRegion());
    formData.append('language', getLanguage());
    Object.keys(itemData).forEach(key => {
        if (key === 'images') {
            itemData.images.forEach(image => {
                formData.append('images', image);
            });
        } else if (key === 'looking_for') {
            formData.append('looking_for', JSON.stringify(itemData[key]));
        } else {
            formData.append(key, itemData[key]);
        }
    });

    return request('/items', {
        method: 'POST',
        body: formData,
    });
};

export const updateItem = (id, itemData) => {
    const formData = new FormData();
    Object.keys(itemData).forEach(key => {
        if (key === 'images') {
            itemData.images.forEach(image => {
                if (image instanceof File) {
                    formData.append('images', image);
                }
            });
        } else if (key === 'looking_for') {
            formData.append('looking_for', JSON.stringify(itemData[key]));
        } else {
            formData.append(key, itemData[key]);
        }
    });

    return request(`/items/${id}`, {
        method: 'PUT',
        body: formData,
    });
};

export const deleteItem = (id) => request(`/items/${id}`, {
    method: 'DELETE',
});

export const scrapeItemData = (url) => request('/items/scrape', {
    method: 'POST',
    body: JSON.stringify({ url }),
});

export const getMyItems = () => request('/items/my');

export const featureItem = (id) => request(`/items/${id}/feature`, {
    method: 'POST',
});

export const getMutualMatches = () => {
    if (!getToken()) return Promise.resolve({ matches: [] });
    return request('/items/matches');
};

const getLocationParams = () => {
    try {
        const stored = localStorage.getItem('user_geo_location');
        if (stored) {
            const { latitude, longitude, userLocation } = JSON.parse(stored);
            let params = '';
            if (latitude && longitude) params += `&latitude=${latitude}&longitude=${longitude}`;
            if (userLocation) params += `&userLocation=${encodeURIComponent(userLocation)}`;
            return params;
        }
    } catch (e) {}
    return '';
};

// Attempt browser geolocation once silently
if (typeof window !== 'undefined' && navigator.geolocation && !localStorage.getItem('user_geo_location')) {
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            try {
                localStorage.setItem('user_geo_location', JSON.stringify({
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude
                }));
            } catch (e) {}
        },
        () => {},
        { timeout: 5000 }
    );
}

export const getPopularItems = (limit = 10) => request(`/items/popular?limit=${limit}&region=${getRegion()}${getLocationParams()}`);
export const getFeaturedItems = (limit = 10, category = 'all') => request(`/items/featured?limit=${limit}&category=${category}&region=${getRegion()}${getLocationParams()}`);

export const getSuggestedItems = (limit = 10) => request(`/items/suggested?limit=${limit}&region=${getRegion()}${getLocationParams()}`);

export const getUserItems = (userId) => request(`/items/user/${userId}`);

// Trades
export const createTrade = (data) => request('/trades', {
    method: 'POST',
    body: JSON.stringify(data),
});

export const getSentTrades = () => request('/trades/sent');
export const getReceivedTrades = () => request('/trades/received');
export const getTradeById = (id) => request(`/trades/${id}`);
export const updateTradeStatus = (id, status) => request(`/trades/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
});

export const counterTrade = (id, data) => request(`/trades/${id}/counter`, {
    method: 'PUT',
    body: JSON.stringify(data),
});

// Conversations
export const getConversations = () => request('/conversations');
export const getConversation = (id) => request(`/conversations/${id}`);

export const createConversation = (data) => request('/conversations', {
    method: 'POST',
    body: JSON.stringify(data),
});

export const startSupportChat = () => request('/conversations/support', {
    method: 'POST',
});

export const getMessages = (conversationId) => request(`/conversations/${conversationId}/messages`);

export const sendMessage = (conversationId, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
    });

    return request(`/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: formData,
    });
};

export const uploadMessageMedia = (formData) => request('/media/messages', {
    method: 'POST',
    body: formData,
});

// Admin
export const getAllUsers = () => request('/admin/users');
export const getAllItems = () => request('/admin/items');
export const getAllTrades = () => request('/admin/trades');
export const getPlatformStats = () => request('/admin/stats');
export const getOnlineUsers = () => request('/admin/online-users');
export const getSupportConversations = () => request('/admin/support');

export const resolveSupportRequest = (id) => request(`/admin/support/${id}/resolve`, {
    method: 'PUT',
});

export const sendAdminEmail = (data) => request('/admin/send-email', {
    method: 'POST',
    body: JSON.stringify(data),
});

export const getUserLogs = (id) => request(`/admin/users/${id}/logs`);

export const updateUserCoins = (id, coins) => request(`/admin/users/${id}/coins`, {
    method: 'PUT',
    body: JSON.stringify({ coins }),
});

export const updateUserRole = (id, role) => request(`/admin/users/${id}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
});

export const getPendingItems = () => request('/items/pending');
export const approveItem = (id) => request(`/items/${id}/approve`, { method: 'PUT' });
export const rejectItem = (id) => request(`/items/${id}/reject`, { method: 'PUT' });

export const getExchangeRate = (currency = 'USD') => request(`/exchange-rate/latest/${currency}`);

export const likeUserApi = (id) => request(`/auth/users/${id}/like`, { method: 'POST' });
export const dislikeUserApi = (id) => request(`/auth/users/${id}/dislike`, { method: 'POST' });
